import {
  FormControl,
  TextField,
  Typography,
  Select,
  MenuItem,
  Grid,
  Button,
  InputLabel,
  FormHelperText,
  IconButton,
  Avatar
} from '@mui/material';

import {genders} from '@/constants';

import {Formik, Form} from 'formik';

import {addYears, startOfToday} from 'date-fns';

import UserValidationSchema from '@/schemas/user';

import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {useCallback, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {NAME_MAX_LENGTH, DESC_MAX_LENGTH} from '@/schemas/user';

import {useDispatch} from 'react-redux';

import {
  setUserInfosFormData,
  resetUserInfosFormData,
  UserInfosFormValues,
  setUserInfos
} from '@/redux-store/slices/user-infos';
import {useAppSelector} from '@/redux-store/store';

import {updateUserData} from '@/rest/user/update-user-data';

import PhotoCamera from '@mui/icons-material/PhotoCamera';

import {LocationInput} from '@/components/forms/input/location';

import {fetchUserData} from '@/rest/user/fetch-user-data';
import {Delete} from '@mui/icons-material';
import {UserFormData} from '@/types/user';
import TypographyTitle from '@/components/ui/typography-title';
import FormLabel from '@/components/ui/form-label';

interface userInfosFormProps {
  onCancel?: () => void;
  onSubmit?: (updatedInfos: UserFormData) => void;
  onError?: (error: Error) => void;
}

const UserInfosForm: React.FC<userInfosFormProps> = ({
  onCancel,
  onSubmit,
  onError
}) => {
  const bDayMax = addYears(startOfToday(), -16).toISOString();
  const bDayMin = addYears(startOfToday(), -99).toISOString();

  const {t} = useTranslation();

  const userValidationSchema = UserValidationSchema(t);

  const initialValues = useAppSelector(
    (state) => state.userInfos.userInfosForm
  );
  const userData = useAppSelector((state) => state.userInfos.userInfos);

  const dispatch = useDispatch();
  const [fileSizeError, setFileSizeError] = useState<string | null>(null);
  const [userInitials, setUserInitials] = useState('');

  const handleImageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        const file = event.target.files[0];
        const fileSize = file.size / 1024 / 1024;
        if (fileSize > 0.3) {
          setFileSizeError(t('file_size_error', {maxSize: 300, unit: 'kB'}));
          return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(event.target.files[0]);
        reader.onload = () => {
          setFileSizeError(null);
          dispatch(
            setUserInfosFormData({
              picture: reader.result as string,
              avatarDeleted: false
            })
          );
        };
      }
    },
    [dispatch, t]
  );

  const handleDeleteImage = useCallback(() => {
    setFileSizeError(null);
    dispatch(
      setUserInfosFormData({
        picture: null,
        avatarDeleted: true
      })
    );
  }, [dispatch]);

  const handleSubmit = useCallback(
    (values: UserInfosFormValues) => {
      const {_id, name, description, location, birthday, gender} = values;

      let updatedValues: UserFormData = {
        _id: _id,
        name: name,
        description: description,
        hometown: '',
        geolocation: null,
        birthday: birthday,
        gender: gender ? gender : 'NA',
        sports: initialValues.sports
      };

      if (location && location !== null) {
        updatedValues.geolocation = {
          coordinates: [location.lng, location.lat],
          type: 'Point'
        };
        updatedValues.hometown = location.label;
      } else if (location === null) {
        updatedValues.geolocation = null;
        updatedValues.hometown = '';
      }

      if (initialValues.picture) {
        updatedValues = {...updatedValues, picture: initialValues.picture};
      }
      if (initialValues.avatarDeleted) {
        updatedValues = {...updatedValues, picture: null};
      }

      updateUserData(updatedValues)
        .then((response) => {
          if (response) {
            if (onSubmit) {
              onSubmit(response);
            }
            if (values.picture && !values.avatarDeleted) {
              fetchUserData()
                .then((newUserValues) => {
                  const pictureSrc = `${
                    newUserValues?.picture
                  }?timestamp=${Date.now()}`;
                  if (newUserValues) {
                    dispatch(
                      setUserInfos({
                        picture: pictureSrc
                      })
                    );
                  }
                })
                .catch((error) => {
                  console.error('Error fetching updated user data:', error);
                });
            } else if (values.avatarDeleted) {
              dispatch(
                setUserInfos({
                  avatarDeleted: true
                })
              );
            }
          } else {
            if (onError) {
              onError(new Error('Error updating user'));
            }
          }
        })
        .catch((error) => {
          console.error('Error:', error);
          if (onError) {
            onError(error as Error);
          }
        });
    },
    [dispatch, initialValues, onError, onSubmit]
  );

  const resetFormData = useCallback(() => {
    dispatch(resetUserInfosFormData());
  }, [dispatch]);

  const computedInitialState = {
    ...initialValues
  };

  const userPictureFromReduxAltered = `${
    userData.picture
  }?timestamp=${Date.now()}`;

  useEffect(() => {
    const nameParts = initialValues.name.split(' ');
    let initials;

    if (nameParts.length === 1) {
      initials = nameParts[0].charAt(0).toUpperCase();
    } else {
      initials = nameParts
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('');
    }

    setUserInitials(initials);
  }, [initialValues.name]);

  return (
    <>
      <Formik
        // enableReinitialize={true}
        initialValues={computedInitialState}
        validationSchema={userValidationSchema}
        validateOnBlur={true}
        validateOnChange={true}
        onSubmit={(values) => {
          handleSubmit(values);
          dispatch(setUserInfosFormData(values));
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          resetForm
        }) => {
          //console.log(errors);

          //  console.log('values: ', values);

          return (
            <Form>
              <TypographyTitle textKey="update_user_data" />
              <Grid container spacing={2} my={1}>
                <Grid item xs={12}>
                  <TextField
                    name="name"
                    label={t('prop_user_name')}
                    value={values.name}
                    onChange={(e) => {
                      handleChange(e);
                      dispatch(
                        setUserInfosFormData({
                          name: e.target.value
                        })
                      );
                    }}
                    onBlur={handleBlur}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    fullWidth
                  />
                  <FormHelperText>
                    {values.name.length}/ {NAME_MAX_LENGTH}
                  </FormHelperText>
                </Grid>
              </Grid>
              <Grid container spacing={2} my={1}>
                <Grid item xs={12}>
                  <TextField
                    name="description"
                    label={<FormLabel text={'prop_user_description'} />}
                    value={values.description}
                    onChange={(e) => {
                      handleChange(e);
                      dispatch(
                        setUserInfosFormData({
                          description: e.target.value
                        })
                      );
                    }}
                    onBlur={handleBlur}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                    fullWidth
                    multiline
                    maxRows={4}
                  />
                  <FormHelperText>
                    {values.description.length} / {DESC_MAX_LENGTH}
                  </FormHelperText>
                </Grid>
              </Grid>

              <Grid container spacing={2} my={1}>
                <Grid item xs={12}>
                  <LocationInput
                    name="location"
                    label={t('hometown')}
                    optional
                    value={values.location}
                    error={errors['location']}
                    touched={touched['location']}
                    onBlur={() => {
                      setFieldTouched('location', true);
                    }}
                    onChange={(location) => {
                      if (location === null) {
                        setFieldValue('location', null);
                        dispatch(
                          setUserInfosFormData({
                            location: null,
                            hometown: ''
                          })
                        );
                      } else {
                        setFieldValue('location', location);
                        dispatch(
                          setUserInfosFormData({
                            location: location,
                            hometown: location.label
                          })
                        );
                      }
                    }}
                    showOnlyCity={true}
                    initialLocation={computedInitialState?.location}
                    allowClear={true}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} mt={3}>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label={t('birthday')}
                    value={values.birthday ? new Date(values.birthday) : null}
                    slotProps={{
                      textField: {
                        error: errors.birthday ? true : false,
                        helperText: errors.birthday
                      }
                    }}
                    onChange={(date) => {
                      const isoDate = date ? date.toISOString() : '';
                      setFieldValue('birthday', date);

                      dispatch(
                        setUserInfosFormData({
                          birthday: isoDate
                        })
                      );
                    }}
                    maxDate={new Date(bDayMax)}
                    minDate={new Date(bDayMin)}
                    sx={{
                      width: '100%',
                      mb: {xs: 1, sm: 0}
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl
                    fullWidth
                    error={touched.gender && Boolean(errors.gender)}
                    variant="outlined"
                    sx={{
                      mt: {xs: 2, sm: 0}
                    }}
                  >
                    <InputLabel>{t('gender')}</InputLabel>
                    <Select
                      name="gender"
                      label={t('gender')}
                      value={values.gender}
                      onChange={(e) => {
                        handleChange(e);
                        dispatch(
                          setUserInfosFormData({
                            gender: e.target.value
                          })
                        );
                      }}
                      onBlur={handleBlur}
                    >
                      {genders.map((gender) => (
                        <MenuItem key={gender.value} value={gender.value}>
                          {t(gender.value)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {touched.gender && errors.gender}
                    </FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>

              <Grid container spacing={2} my={3} justifyContent={'center'}>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    align="center"
                    sx={{marginBottom: 2}}
                  >
                    {t('user_avatar')}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{display: 'flex', justifyContent: 'center'}}
                >
                  <Button
                    component="span"
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={handleDeleteImage}
                    sx={{
                      display: {xs: 'none', sm: 'flex'},
                      textTransform: 'none'
                    }}
                  >
                    {t('delete_photo')}
                  </Button>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  <label htmlFor="avatar-upload">
                    <Button
                      component="span"
                      variant="outlined"
                      color="secondary"
                      startIcon={<PhotoCamera />}
                      sx={{
                        display: {xs: 'none', sm: 'flex'},
                        textTransform: 'none'
                      }}
                    >
                      {t('upload_photo')}
                    </Button>
                  </label>
                  <input
                    id="avatar-upload"
                    type="file"
                    onChange={handleImageChange}
                    accept=".jpg, .jpeg, .png"
                    hidden
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginRight: 2,
                    marginBottom: 1
                  }}
                >
                  <label htmlFor="avatar-delete-xs">
                    <IconButton
                      aria-label={t('delete_user_avatar')}
                      color="error"
                      onClick={handleDeleteImage}
                      component="span"
                      sx={{
                        display: {xs: 'flex', sm: 'none'}
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </label>

                  <label htmlFor="avatar-upload-xs">
                    <IconButton
                      aria-label={t('upload_user_avatar')}
                      color="secondary"
                      component="span"
                      sx={{
                        display: {xs: 'flex', sm: 'none'},
                        marginLeft: 4,
                        marginTop: '1px'
                      }}
                    >
                      <PhotoCamera />
                    </IconButton>
                  </label>
                  <input
                    id="avatar-upload-xs"
                    type="file"
                    onChange={handleImageChange}
                    accept=".jpg, .jpeg, .png"
                    hidden
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginRight: 2,
                    marginBottom: 1
                  }}
                >
                  {!computedInitialState.avatarDeleted ? (
                    <Avatar
                      sx={{
                        width: '100px',
                        height: '100px',
                        marginTop: {xs: 2, sm: 0}
                      }}
                    >
                      <img
                        src={
                          computedInitialState.picture ||
                          userPictureFromReduxAltered
                        }
                        alt={t('user_avatar')}
                        style={{
                          height: '100%',
                          width: '100%'
                        }}
                      />
                    </Avatar>
                  ) : (
                    <Avatar
                      alt={t('user_avatar')}
                      sx={{
                        width: '100px',
                        height: '100px',
                        objectFit: 'cover',
                        marginTop: {xs: 2, sm: 0},
                        backgroundColor: computedInitialState.color
                      }}
                    >
                      {userInitials}
                    </Avatar>
                  )}
                </Grid>
              </Grid>

              {fileSizeError && (
                <Typography align="center" color="error">
                  {fileSizeError}
                </Typography>
              )}

              <Grid
                container
                direction={{xs: 'column-reverse', sm: 'row'}}
                spacing={2}
                sx={{
                  my: {xs: 1, sm: 3}
                }}
              >
                <Grid
                  item
                  xs={12}
                  sm={4}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: {xs: 2, sm: 0},
                    marginTop: {xs: 1, sm: 0}
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={async () => {
                      resetFormData();
                      resetForm({
                        values: userData
                      });
                      if (onCancel) {
                        onCancel();
                      }
                    }}
                    sx={{
                      width: {xs: '50%', sm: '80%'}
                    }}
                  >
                    {t('cancel')}
                  </Button>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={4}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: {xs: 2, sm: 0},
                    marginTop: {xs: 1, sm: 0}
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      resetFormData();
                      resetForm({
                        values: userData
                      });
                    }}
                    sx={{
                      width: {xs: '50%', sm: '80%'}
                    }}
                  >
                    {t('reset')}
                  </Button>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={4}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: {xs: 2, sm: 0},
                    marginTop: {xs: 1, sm: 0}
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!!fileSizeError}
                    onClick={() => {
                      handleSubmit();
                    }}
                    sx={{
                      width: {xs: '50%', sm: '80%'}
                    }}
                  >
                    {t('save')}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default UserInfosForm;
